// src/features/faq/components/GiftCardsFAQs.tsx

import React from 'react';
import { CreditCard, Send, Settings } from 'lucide-react';

export interface GiftCardsFAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface GiftCardsFAQData {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: GiftCardsFAQItem[];
}

export const GiftCardsFAQs = (navigateCallback: (path: string) => void): GiftCardsFAQData => {
  return {
    id: 'gift-cards',
    title: 'Gift Cards',
    icon: <CreditCard className="h-5 w-5 text-pink-500" />,
    description: 'Regala puntos con una tarjeta personalizada que llega por WhatsApp.',
    faqs: [
      {
        id: 'giftcards-what',
        question: '¿Qué es una gift card y cómo envío una?',
        answer: (
          <div>
            <p>Una gift card es un regalo de puntos: tú (o un cliente que quiera regalar) eliges a quién va y cuántos puntos lleva, y al destinatario le llega por WhatsApp una tarjeta con su nombre, de parte de quién va y los puntos — junto con un mensaje de tu negocio.</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a la sección Gift Cards.</li>
              <li>Elige el destinatario (cliente existente o nueva persona).</li>
              <li>Indica de parte de quién va el regalo.</li>
              <li>Elige la cantidad de puntos y el diseño.</li>
              <li>Opcional: pulsa "Ver preview" para ver la tarjeta ya llena.</li>
              <li>Envía. El mensaje sale por el WhatsApp de tu negocio.</li>
            </ol>
            <div className="mt-2">
              <button onClick={() => navigateCallback('/dashboard/gift-cards')} className="inline-flex items-center text-sm text-accent hover:text-blue-800 dark:text-blue-300">
                <Send className="h-4 w-4 mr-1.5" />
                Ir a Gift Cards
              </button>
            </div>
          </div>
        )
      },
      {
        id: 'giftcards-existing-vs-new',
        question: '¿Qué diferencia hay entre "Cliente existente" y "Nueva persona"?',
        answer: (
          <div>
            <p>Al crear la gift card eliges uno de los dos modos:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Cliente existente:</strong> ya está registrado en tu programa. Lo buscas por nombre o teléfono y los puntos <strong>se abonan a su cuenta al instante</strong>.</li>
              <li><strong>Nueva persona:</strong> aún no es cliente. Capturas su teléfono (y su nombre, si lo tienes) y los puntos quedan <strong>pendientes</strong>: se abonan solos en cuanto esa persona se registre en tu kiosko con ese mismo teléfono. No tienes que hacer nada más.</li>
            </ul>
            <p className="mt-2 text-content-2">En ambos casos el mensaje de WhatsApp con la tarjeta se envía de inmediato.</p>
          </div>
        )
      },
      {
        id: 'giftcards-amounts',
        question: '¿Puedo regalar cualquier cantidad de puntos?',
        answer: (
          <div>
            <p>Sí. Además de los botones con cantidades rápidas (10, 20, 50, 100, 500 y 1000), está el botón <strong>"Personalizado"</strong>, donde puedes escribir la cantidad exacta que quieras. No necesitas pedir que te agreguen cantidades nuevas.</p>
          </div>
        )
      },
      {
        id: 'giftcards-designs',
        question: '¿Qué diseños de tarjeta hay? ¿Puedo usar el diseño de mi negocio?',
        answer: (
          <div>
            <p>Hay 6 diseños listos para usar (General, Cumpleaños, Día de las Madres, Navidad, Agradecimiento y VIP), que se personalizan solos con el nombre, el remitente y los puntos.</p>
            <p className="mt-2">Si tu negocio tiene su <strong>diseño propio</strong> dado de alta, aparece como la opción <strong>⭐</strong> (preseleccionada): es el arte de tu propia tarjeta y también se llena solo en cada envío. Si quieres dar de alta o cambiar tu diseño propio, contáctanos y lo preparamos por ti.</p>
          </div>
        )
      },
      {
        id: 'giftcards-preview',
        question: '¿Puedo ver la tarjeta antes de enviarla?',
        answer: (
          <div>
            <p>Sí. En la sección de diseños está el botón <strong>"Ver preview"</strong>: genera la tarjeta exactamente como va a llegar, ya con el nombre, el remitente y los puntos de ese envío. Si algo no te convence, cambias los datos o el diseño y vuelves a previsualizar.</p>
          </div>
        )
      },
      {
        id: 'giftcards-message-config',
        question: '¿Cómo cambio el mensaje de WhatsApp que acompaña la tarjeta?',
        answer: (
          <div>
            <p>En la <strong>configuración de Gift Cards</strong> puedes editar la plantilla del mensaje (con variables como @nombre, @remitente, @puntos, @negocio y @direccion), el enlace de Google Maps de tu ubicación, y hacer envíos de prueba a tu propio teléfono.</p>
            <p className="mt-2 text-content-2">Consejo: usa el enlace corto de Google Maps (el que empieza con maps.app.goo.gl) para que en WhatsApp siempre salga como enlace clickeable.</p>
            <div className="mt-2">
              <button onClick={() => navigateCallback('/dashboard/gift-cards?tab=ajustes')} className="inline-flex items-center text-sm text-accent hover:text-blue-800 dark:text-blue-300">
                <Settings className="h-4 w-4 mr-1.5" />
                Ir a Gift Cards → Ajustes
              </button>
            </div>
          </div>
        )
      },
      {
        id: 'giftcards-cancel',
        question: '¿Puedo cancelar una gift card enviada?',
        answer: (
          <div>
            <p>Las gift cards <strong>pendientes</strong> (enviadas a alguien que aún no se registra) sí se pueden cancelar para que los puntos ya no se abonen — por ahora la cancelación la hace el equipo de Lealify: contáctanos con el teléfono del destinatario y la anulamos.</p>
            <p className="mt-2">Si el destinatario ya era cliente, los puntos se abonaron al instante; en ese caso ya no se cancela la tarjeta, pero puedes ajustar sus puntos desde su ficha de cliente si fue un error.</p>
          </div>
        )
      },
    ],
  };
};
